// React Dependencies
import React, { useState } from 'react';

// External Dependencies
import { Button, TextField } from '@material-ui/core';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

// Internal Dependencies
import apiService from '../../../services.js/apiService';
import Constants from '../../../common/constants';
import OverLoading from '../../../components/global/OverLoading';
import { updateModalContent, toggleModal } from '../../../actions/modal';
import { IsEmpty } from '../../../common/checks';
import { Sleep } from '../../../common/util';
import Colours from '../../../styles/colours';

const ClearCache = (props) => {
  const cacheType = [
    { name: Constants.CacheTypeName.clearAllCache,  param: 'clearAllCache', text: Constants.CacheTypeText.clearAllCache, needsId: false },
    { name: Constants.CacheTypeName.clearSpecificCache, param: null, text: Constants.CacheTypeText.clearSpecificCache, needsId: false },
    { name: Constants.CacheTypeName.highlights, param: 'highlights', text: Constants.CacheTypeText.highlights, needsId: false },
    { name: Constants.CacheTypeName.ads, param: 'ads', text: Constants.CacheTypeText.ads, needsId: false },
    { name: Constants.CacheTypeName.searchTrends, param: 'searchTrends', text: Constants.CacheTypeText.searchTrends, needsId: false },
    { name: Constants.CacheTypeName.specificPostById, param: 'post', text: Constants.CacheTypeText.specificPostById, needsId: true },
    { name: Constants.CacheTypeName.allPostKeys, param: 'allPostKeys', text: Constants.CacheTypeText.allPostKeys, needsId: false },
    { name: Constants.CacheTypeName.specificPageById, param: 'page', text: Constants.CacheTypeText.specificPageById, needsId: true },
    { name: Constants.CacheTypeName.allPageKeys, param: 'allPageKeys', text: Constants.CacheTypeText.allPageKeys, needsId: false },
    { name: Constants.CacheTypeName.allPublicPostKeys, param: 'allPublicPostKeys', text: Constants.CacheTypeText.allPublicPostKeys, needsId: false },
  ];
  const [postId, setPostId] = useState('');
  const [pageId, setPageId] = useState('');
  const [cacheKey, setCacheKey] = useState('');
  const [isPostError, setIsPostError] = useState(false);
  const [isPageError, setIsPageError] = useState(false);
  const [isClearCacheKeyError, setIsClearCacheKeyError] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleCache = async (type) => {
    try {
      setLoading(true);
      if (type.name === Constants.CacheTypeName.clearAllCache) {
        await apiService.clearMobileAppCache();
      } else if (type.name === Constants.CacheTypeName.clearSpecificCache) {
        await apiService.clearSpecificMobileAppCache(cacheKey);
        setCacheKey('');
        setIsClearCacheKeyError(false);
      } else {
        await apiService.clearSpecificMobileAppCache(`${type.param}${type.needsId
          ? `:${type.name === Constants.CacheTypeName.specificPostById ? postId : pageId}` : ''}`);
        setPostId('');
        setPageId('');
        setIsPostError(false);
        setIsPageError(false);
      }

      toast.success('Cache Cleared');
      await Sleep(1000);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (type) => {
    if (type.name === Constants.CacheTypeName.clearSpecificCache && IsEmpty(cacheKey)) {
      setIsClearCacheKeyError(true);
      return;
    }

    if (type.name === Constants.CacheTypeName.specificPostById && IsEmpty(postId)) {
      setIsPostError(true);
      return;
    }

    if (type.name === Constants.CacheTypeName.specificPageById && IsEmpty(pageId)) {
      setIsPageError(true);
      return;
    }

    props.updateModalContent({
      title:        `Are you sure you want to clear ${type.name === 'clearAllCache' ? 'All cache' : type.text}?`,
      content:      `This will clear the cache and action is irreversible`,
      actionButton: {
        actionText: 'Yes',
        actionType: 'danger',
        action:     () => handleCache(type),
      },
      cancelText:       'No',
      closeAfterAction: true,
    });

    props.toggleModal();
  };

  const handleFieldChange = (e) => {
    switch (e.target.name) {
      case Constants.CacheTypeName.specificPostById:
        setPostId(e.target.value);
        setIsPostError(IsEmpty(e.target.value));
        break;
      case Constants.CacheTypeName.specificPageById:
        setPageId(e.target.value);
        setIsPageError(IsEmpty(e.target.value));
        break;
      case Constants.CacheTypeName.clearSpecificCache:
        setCacheKey(e.target.value);
        setIsClearCacheKeyError(IsEmpty(e.target.value));
        break;
      default:
    }
  };

  return (
    <div>
      {loading && <OverLoading />}
      <div className="content-header">
        <h1>Clear Mobile/Web App Cache</h1>
      </div>

      <div>
        <div className="text-center my-5">
          <h4 className="my-5">Select cache to clear</h4>
        </div>

        <div className="container d-block mx-auto" style={{ maxWidth: 650 }}>

          {
            cacheType.map((type) => (
              <div key={type.title} className="row mb-3 align-items-center py-3 px-3" style={{ background: Colours.LightGray, border: '1px solid #dbdbdb', borderRadius: 6 }}>
                <div className="col-6">
                  <span style={{ fontWeight: 'bold', fontSize: 14 }}>{type.text}</span>
                </div>

                <div className="col-6 text-right">
                  {
                    (type.name === Constants.CacheTypeName.clearSpecificCache) && (
                      <div className="float-left mb-3 mb-md-0">
                        <TextField
                          name={type.name}
                          placeholder="Enter cache key"
                          onChange={handleFieldChange}
                          error={isClearCacheKeyError}
                          value={cacheKey}
                        />
                      </div>
                    )
                  }
                  {
                    (type.name === Constants.CacheTypeName.specificPostById || type.name === Constants.CacheTypeName.specificPageById)
                    && (
                      <div className="float-left mb-3 mb-md-0">
                        <TextField
                          name={type.name}
                          placeholder="Enter ID"
                          type="number"
                          InputProps={{ inputProps: { min: 0 } }}
                          onChange={handleFieldChange}
                          error={type.name === Constants.CacheTypeName.specificPostById ? isPostError : isPageError}
                          value={type.name === Constants.CacheTypeName.specificPostById ? postId : pageId}
                        />
                      </div>
                    )
                  }

                  <div className="float-right">
                    <Button
                      variant="contained"
                      color="secondary"
                      className="px-4"
                      onClick={() => handleClick(type)}
                      disabled={loading}
                    >
                      Clear
                    </Button>
                  </div>
                </div>
              </div>
            ))
          }

        </div>
      </div>
    </div>
  );
};

ClearCache.propTypes = {
  updateModalContent: PropTypes.func.isRequired,
  toggleModal:        PropTypes.func.isRequired,
};

export default connect(null, { toggleModal, updateModalContent })(ClearCache);
